import { useNavigate } from "react-router-dom";
import Button from "../UI/Button";
import Card from "../UI/Card";
import Text from "../UI/Text";
import Title from "../UI/Title";
import ContentLayout from "../UI/Layout/ContentLayout";
import HeaderLayout from "../UI/Layout/HeaderLayout";
import TenantProfileList from "../Profile/TenantProfileList";
import GuidedTour from "../GuidedTour/GuidedTour";
// @ts-expect-error pure js
import CandidaterForm from "../Tenant/CandidaterForm";
import {
  ArrowDownRight,
  HousePrivatif,
  Pieces,
  Surface,
  // @ts-expect-error pure js
} from "../UI/Icons";
// @ts-expect-error pure js
import DisplayField from "../UI/DisplayField";
// @ts-expect-error pure js
import IconAndText from "../UI/IconAndText";
// @ts-expect-error pure js
import PropertyShowCandidat from "../Property/PropertyShowCandidat";
// @ts-expect-error pure js
import DescriptionFinance from "../Property/Show/DescriptionFinance";
// @ts-expect-error pure js
import { GoToBien } from "./GoToBien";
// @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx";
// @ts-expect-error pure js
import { classnames, TextOneOrMany } from "../../utils";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import { useCandidatProfiles } from "../../hooks/use-profiles-indiv";
import type { Property } from "../../models/property";
import type { Profile } from "../Profile/types";
import style from "./CandidatShow.module.css";

const DOSSIER_STEPS: string[] = [
  "Complétez votre état civil (nom, prénom, date et lieu de naissance)",
  "Indiquez votre adresse actuelle",
  "Renseignez votre situation professionnelle (type de contrat, revenus)",
  "Ajoutez une caution ou un garant (garantie Visale, parents, employeur, etc.)",
];

const JUSTIFICATIFS_STEPS: string[] = [
  "Justificatif d'identité : copie recto-verso de votre carte d'identité OU passeport OU titre de séjour",
  "Justificatif de domicile : facture d'électricité OU attestation d'hébergement OU 3 dernières quittances de loyer OU autre document",
  "Justificatif de revenus : 3 derniers bulletins de salaire ET copie contrat de travail ET dernier avis d'imposition, ou tout autre justificatif",
];

interface CandidatShowProps {
  property?: Property;
}

function GuideDetails({ items }: { items: string[] }) {
  return (
    <div className={style.guideDetails}>
      {items.map((item) => (
        <IconAndText
          key={item}
          icon={<ArrowDownRight />}
          text={<Text>{item}</Text>}
        />
      ))}
    </div>
  );
}

export default function CandidatShow({ property }: CandidatShowProps) {
  const navigate = useNavigate();
  const { tenants, isLoading: isLoadingTenants } = useTenants({
    isLocataire: true,
  });
  const [CandidateModal, closeCandidateModal, openCandidateModal] =
    useModal(false);
  const [BienModal, closeBienModal, openBienModal] = useModal(false);

  const {
    profiles,
    structProfiles,
    removeGarantor,
    deleteProfile,
    isLoading: isLoadingProfiles,
    error: errorProfiles,
  } = useCandidatProfiles();

  async function deleteProfileHandler(profileId: number) {
    if (window.confirm("Voulez-vous vraiment supprimer ce profil ?")) {
      try {
        await deleteProfile(profileId);
      } catch (error) {
        console.error(error);
      }
    }
  }

  const bienSearchParam = property ? `?bien=${property.id}` : "";

  const showProfileTour =
    profiles.length === 0 && !isLoadingProfiles && !errorProfiles;
  const showCandidatureTour =
    !showProfileTour && tenants?.length === 0 && !isLoadingTenants;

  return (
    <>
      <GuidedTour
        guideId="candidat-profile"
        run={showProfileTour}
        steps={[
          {
            target: "[data-joyride='profile']",
            title: "Bienvenue sur Qalimo.",
            content:
              "Qalimo permet de constituer votre dossier locataire, et de postuler au bien envoyé par votre bailleur. La 1ère étape est de créer votre profil, et d'y intégrer vos documents.",
          },
        ]}
      />
      <GuidedTour
        guideId="candidat-candidatures"
        run={showCandidatureTour}
        steps={[
          {
            target: "[data-joyride='candidatures']",
            title: "Soumettez votre candidature",
            content:
              'Votre dossier de candidature est prêt. Cliquez simplement sur "Envoyer ma candidature" pour la transmettre au bailleur.',
          },
        ]}
      />
      <ContentLayout
        header={
          property ? (
            <PropertyShowCandidat property={property} />
          ) : (
            <HeaderLayout
              title="Espace candidat"
              subtitle="Envoyez votre dossier de candidature de manière 100% dématérialisée"
            />
          )
        }
        body={
          <>
            <CandidateModal>
              <CandidaterForm
                profiles={structProfiles}
                propertyId={property?.id}
                onSubmitted={() => {
                  closeCandidateModal();
                  navigate("/locataire/candidatures");
                }}
              />
            </CandidateModal>
            <BienModal>
              <GoToBien onSubmitted={closeBienModal} />
            </BienModal>
            <div className={classnames("content", style.body)}>
              {property && (
                <>
                  <Button
                    onClick={openCandidateModal}
                    data-joyride="candidatures"
                    className={style.candidaterAction}
                  >
                    Envoyer ma candidature
                  </Button>
                  <Card>
                    <div className={style.specs}>
                      <DisplayField
                        icon={<Surface />}
                        fieldValue="Superficie habitable"
                        fieldName={
                          property.surface_total
                            ? `${property.surface_total}m²`
                            : "Non renseigné"
                        }
                      />
                      <DisplayField
                        icon={<Pieces />}
                        fieldValue="Nombre de pièces"
                        fieldName={
                          property.nb_pieces_principales || "Non renseigné"
                        }
                      />
                      <DisplayField
                        icon={<HousePrivatif />}
                        fieldValue="Parties à usage privatif"
                        fieldName={
                          property.locaux_privatifs?.length
                            ? property.locaux_privatifs.join(", ")
                            : "Non renseigné"
                        }
                      />
                    </div>
                  </Card>
                  <Card>
                    <DescriptionFinance property={property} />
                  </Card>
                  {property.description_public && (
                    <Card>
                      <Text as="pre" className={style.description}>
                        {property.description_public}
                      </Text>
                    </Card>
                  )}
                </>
              )}
              <div className={style.sectionHead}>
                <div className={style.sectionTitle}>
                  <Title as="h2" variant="deca" weight="bold">
                    <TextOneOrMany
                      one={"Votre dossier de candidature"}
                      many={"Vos dossiers de candidature"}
                      number={structProfiles.length}
                    />
                  </Title>
                </div>
                <div className={style.sectionActions}>
                  {!property && (
                    <Button variant="secondary" onClick={() => openBienModal()}>
                      Postuler à un bien
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      navigate(`add${bienSearchParam}`);
                    }}
                    data-joyride="profile"
                  >
                    Ajouter un profil candidat
                  </Button>
                </div>
              </div>
              <TenantProfileList
                profiles={structProfiles as Profile[]}
                editProfileHandler={(profileId) =>
                  navigate(`${profileId}${bienSearchParam}`)
                }
                deleteProfileHandler={deleteProfileHandler}
                removeGarant={removeGarantor}
              />
              <Card>
                <div className={style.guide}>
                  <Title as="h3" variant="hecto" weight="medium">
                    Comment faire son dossier locataire ?
                  </Title>
                  <Text weight="bold">
                    Etape 1 : Créez le profil de TOUS les locataires :
                  </Text>
                  <GuideDetails items={DOSSIER_STEPS} />
                  <Text weight="bold">
                    Etape 2 : Joindre les justificatifs à votre demande :
                  </Text>
                  <GuideDetails items={JUSTIFICATIFS_STEPS} />
                  <Text weight="bold">
                    Etape 3 : Une fois les profils créés, cliquez sur{" "}
                    <Button variant="tertiary" onClick={openBienModal}>
                      Postuler à un bien
                    </Button>{" "}
                    et renseignez le numéro de bien communiqué par le
                    propriétaire.
                  </Text>
                  <Text weight="bold">
                    Etape 4 : Cliquez sur{" "}
                    {property ? (
                      <Button variant="tertiary" onClick={openCandidateModal}>
                        Envoyer ma candidature
                      </Button>
                    ) : (
                      "Envoyer ma candidature"
                    )}
                    .
                  </Text>
                  <Text weight="bold">
                    Etape 5 : Choisissez la date d'emménagement souhaitée et
                    sélectionnez les profils puis validez.
                  </Text>
                  <Text className={style.guideFooter}>
                    Un dossier complet, comprenant l'ensemble des informations
                    et des justificatifs, augmente considérablement vos chances
                    d'être sélectionné !
                  </Text>
                </div>
              </Card>
            </div>
          </>
        }
      />
    </>
  );
}
